import React, { useState, useRef, useEffect, useMemo } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { FaFolderClosed, FaFolderOpen } from 'react-icons/fa6'
import { FiChevronDown, FiChevronRight } from 'react-icons/fi'

function buildFolderTree(folders) {
	const map = new Map()
	const roots = []
	folders.forEach(folder => map.set(folder.id, { ...folder, children: [] }))
	folders.forEach(folder => {
		if (folder.parentId !== null && map.has(folder.parentId)) {
			map.get(folder.parentId).children.push(map.get(folder.id))
		} else if (folder.parentId === null) {
			roots.push(map.get(folder.id))
		}
	})

	//comentar para mostrar todas as pastas
	const individualRoot = roots.find(f => f.private === true)
	if (individualRoot) {
		const seen = new Set()
		individualRoot.children = individualRoot.children.filter(child => {
			if (seen.has(child.name)) return false
			seen.add(child.name)
			return true
		})
	}

	return roots
}

function getFolderNameById(folders, id) {
	const found = folders.find(f => f.id === id)
	return found ? found.name : 'Seleccionar'
}

export function SelectFolder({ folders = [], selectedFolder, onSelect, legend = true, treeType }) {
	const [open, setOpen] = useState(false)
	const [expanded, setExpanded] = useState({})
	const ref = useRef(null)

	const filteredFolders = useMemo(() => {
		if (treeType === 'common') {
			const root = folders.find(f => f.parentId === null && f.private === false)
			if (!root) return []
			const result = [root]
			const stack = [root]
			while (stack.length) {
				const node = stack.pop()
				const children = folders.filter(f => f.parentId === node.id)
				result.push(...children)
				stack.push(...children)
			}
			return result
		}
		if (treeType === 'particular') {
			const individualRoot = folders.find(f => f.parentId === null && f.private === true)
			const uniqueSubfolders = []
			const seenNames = new Set()

			if (individualRoot) {
				folders
					.filter(f => f.parentId === individualRoot.id)
					.forEach(f => {
						if (!seenNames.has(f.name)) {
							uniqueSubfolders.push(f)
							seenNames.add(f.name)
						}
					})
			}

			return uniqueSubfolders
		}

		return folders
	}, [folders, treeType])

	const folderTree = useMemo(() => buildFolderTree(filteredFolders), [filteredFolders])

	useEffect(() => {
		const handleClickOutside = e => {
			if (ref.current && !ref.current.contains(e.target)) setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const handleSelect = (id, name) => {
		onSelect(selectedFolder === id ? null : id, name)
		setOpen(false)
	}

	const toggleExpand = id => {
		setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
	}

	const renderFolders = (folders, level = 0) =>
		folders.map(folder => {
			const hasChildren = folder.children.length > 0
			const isExpanded = expanded[folder.id]
			return (
				<div key={folder.id}>
					<div
						className={`tw-flex tw-items-center tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100 ${
							selectedFolder === folder.id ? 'tw-bg-indigo-50' : ''
						}`}
						onClick={() => handleSelect(folder.id, folder.name)}
					>
						{hasChildren ? (
							<button
								type='button'
								onClick={e => {
									e.stopPropagation()
									toggleExpand(folder.id)
								}}
								className='tw-mr-1 tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-bg-transparent tw-border-none tw-outline-none'
								tabIndex={-1}
								aria-label={isExpanded ? 'Cerrar carpeta' : 'Abrir carpeta'}
							>
								{isExpanded ? <FiChevronDown /> : <FiChevronRight />}
							</button>
						) : (
							<span className='tw-w-5 tw-h-5 tw-inline-block' />
						)}
						{isExpanded && hasChildren ? (
							<FaFolderOpen className='tw-text-gray-500 tw-mr-2 tw-w-4 tw-h-4' style={{ marginLeft: `${level * 12}px` }} />
						) : (
							<FaFolderClosed className='tw-text-gray-500 tw-mr-2 tw-w-4 tw-h-4' style={{ marginLeft: `${level * 12}px` }} />
						)}
						<span className='tw-flex-1 tw-text-sm tw-text-gray-800'>{folder.name}</span>
						{selectedFolder === folder.id && (
							<span className='tw-ml-2 tw-p-1 tw-pr-4'>
								<FaCheckCircle className='tw-text-black' />
							</span>
						)}
					</div>
					{hasChildren && isExpanded && renderFolders(folder.children, level + 1)}
				</div>
			)
		})

	const selectedFolderName = useMemo(() => (selectedFolder ? getFolderNameById(folders, selectedFolder) : 'Seleccionar'), [folders, selectedFolder])

	return (
		<div className='tw-relative tw-w-full' ref={ref}>
			{legend ?? (
				<label className='tw-block tw-font-medium tw-mb-2 tw-text-sm'>
					Carpeta <span className='tw-text-error-700'>*</span>
					<span className='tw-ml-1 tw-text-gray-400 ' title='Selecione uma carpeta'>
						?
					</span>
				</label>
			)}
			<div
				className={`border tw-rounded-lg tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-between tw-bg-white tw-cursor-pointer`}
				style={{ borderColor: `#4F46E5` }}
				onClick={() => setOpen(open => !open)}
			>
				<span className={`tw-text-gray-700 ${selectedFolder ? '' : 'tw-text-gray-500'} tw-text-sm`}>{selectedFolderName}</span>
				<FiChevronDown className='tw-ml-2 tw-text-black' />
			</div>
			{open && (
				<div className='tw-absolute tw-z-10 tw-w-full tw-bg-white border tw-rounded-lg tw-mt-1 tw-shadow-lg tw-max-h-60 tw-overflow-y-auto'>
					{folderTree.length > 0 ? renderFolders(folderTree) : <div className='tw-p-4 tw-text-gray-400'>No folders</div>}
				</div>
			)}
		</div>
	)
}
